import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { FaXTwitter } from "react-icons/fa6";
import { FaInstagram, FaLinkedin, FaMedium } from "react-icons/fa";

type Variant = "desktop" | "mobile";

const FooterSection = ({
    title,
    renderContent,
}: {
    title: string;
    renderContent: (variant: Variant) => ReactNode;
}) => (
    <>
        <div className="hidden md:block space-y-4">
            <h3 className="font-semibold text-lg">{title}</h3>
            {renderContent("desktop")}
        </div>
        <details className="group border-t border-white/10 py-3 md:hidden">
            <summary className="flex items-center justify-between text-left text-sm font-semibold text-white">
                {title}
                <ChevronDown className="h-4 w-4 text-white transition-transform group-open:rotate-180" />
            </summary>
            <div className="mt-3 space-y-3 text-sm text-neutral-200">
                {renderContent("mobile")}
            </div>
        </details>
    </>
);

export const Footer = () => {
    const { t } = useTranslation();
    const addressTitle = t('footer.address_title');
    const hqTitle = t('footer.hq_title');
    const hqAddress = t('footer.hq_address');
    const officeTitle = t('footer.office_title');
    const officeAddress = t('footer.office_address');

    const navLinks = [
        { href: "/#services", label: t("nav.services") },
        { href: "/#partners", label: t("nav.partners") },
        { href: "/#process", label: t("nav.process") },
        { href: "/#regions", label: t("nav.regions") },
        { href: "/#contact", label: t("nav.contact") },
        { href: "/kurumsal", label: t("nav.about_menu.label") },
    ];

    const legalLinks = [
        { href: "/kullanim-sartlari", label: t("footer.legal_links.terms") },
        { href: "/aydinlatma-metni", label: t("footer.legal_links.disclosure") },
        { href: "/on-bilgilendirme-formu", label: t("footer.legal_links.preinfo") },
        { href: "/mesafeli-satis-sozlesmesi", label: t("footer.legal_links.distance") },
        { href: "/kvkk-basvuru-formu", label: t("footer.legal_links.kvkk") },
        { href: "/iptal-iade-kosullari", label: t("footer.legal_links.cancel") },
    ];

    const socialLinks = [
        { href: "https://x.com/insorganizasyon", icon: FaXTwitter },
        { href: "https://www.instagram.com/insorganizasyon", icon: FaInstagram },
        { href: "https://www.linkedin.com/in/insorganizasyon", icon: FaLinkedin },
        { href: "https://medium.com/@insorganization.org", icon: FaMedium },
    ];

    const renderNavLinks = (variant: Variant) => (
        <ul className="space-y-2">
            {navLinks.map(link => (
                <li key={link.href}>
                    <a
                        href={link.href}
                        className={variant === "desktop" ? "text-neutral-300 hover:text-white transition-colors" : "text-neutral-200"}
                    >
                        {link.label}
                    </a>
                </li>
            ))}
        </ul>
    );

    const renderLegalLinks = (variant: Variant) => (
        <ul className="space-y-2">
            {legalLinks.map(link => (
                <li key={link.href}>
                    <a
                        href={link.href}
                        className={variant === "desktop" ? "text-neutral-300 hover:text-white transition-colors" : "text-neutral-200"}
                    >
                        {link.label}
                    </a>
                </li>
            ))}
        </ul>
    );

    const renderSocialLinks = (variant: Variant) => (
        <>
            <div className={variant === "desktop" ? "flex space-x-4" : "flex space-x-4"}>
                {socialLinks.map((link, i) => (
                    <a key={i} href={link.href} className="text-neutral-300 hover:text-white transition-colors">
                        <link.icon className="w-6 h-6" />
                    </a>
                ))}
            </div>
            <div className="mt-6 space-y-2 text-sm">
                <h4 className="font-semibold text-base text-white">{t("footer.legal")}</h4>
                <a href="/privacy" className="text-neutral-300 hover:text-white transition-colors">{t("footer.privacy_policy")}</a>
            </div>
        </>
    );

    return (
        <footer className="bg-brand-navy text-white pt-8 md:pt-12">
            <div className="container mx-auto px-4 py-16">
                <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-[2fr,repeat(4,minmax(0,1fr))] lg:gap-12">
                    {/* Column 1: About */}
                    <div className="space-y-6 md:col-span-2 lg:col-span-1">
                        <p className="text-neutral-300 text-sm leading-relaxed">
                            {t("footer.about")}
                        </p>
                        <div className="space-y-3 text-sm text-neutral-300">
                            <h3 className="font-semibold text-white text-base">{addressTitle}</h3>
                            <div>
                                <h4 className="font-semibold text-white mb-1">{hqTitle}</h4>
                                <p className="whitespace-pre-line leading-relaxed">{hqAddress}</p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-white mb-1">{officeTitle}</h4>
                                <p className="whitespace-pre-line leading-relaxed">{officeAddress}</p>
                            </div>
                        </div>
                    </div>

                    <FooterSection
                        title={t("footer.navigation")}
                        renderContent={(variant) => renderNavLinks(variant)}
                    />
                    <FooterSection
                        title={t("footer.legal")}
                        renderContent={(variant) => renderLegalLinks(variant)}
                    />
                    <FooterSection
                        title={t("footer.follow_us")}
                        renderContent={(variant) => renderSocialLinks(variant)}
                    />
                </div>
            </div>
            <div className="border-t border-white/10 py-6">
                <div className="container mx-auto px-4 text-center text-sm text-neutral-400">
                    © {new Date().getFullYear()} INS Organization. {t("footer.copyright")}
                </div>
            </div>
        </footer>
    );
};
